/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { compose } from 'recompose';

import { Grid, Link, PageGridContainer, PageNotification } from '@coursera/cds-core';

import withCustomLabelsByUserAndCourse from 'bundles/custom-labels/hoc/withCustomLabelsByUserAndCourse';
import type { ReplaceCustomContent } from 'bundles/custom-labels/types/CustomLabels';

import _t from 'i18n!nls/course-cards';

import 'css!./__styles__/Notification';

type Props = {
  messageTypeName?: string | null;
  courseId: string;
  courseName: string;
  courseSlug: string;
  isNotificationVisible?: boolean;
  isRhymeProject?: boolean;
  replaceCustomContent: ReplaceCustomContent;
};

type getMessageAndTypeParam = {
  messageTypeName: string;
  courseName: string;
  courseSlug: string;
  isRhymeProject?: boolean;
  replaceCustomContent: ReplaceCustomContent;
};

const getMessageAndType = ({
  messageTypeName,
  courseName,
  courseSlug,
  isRhymeProject,
  replaceCustomContent,
}: getMessageAndTypeParam): {
  message: React.ReactChild & React.ReactNode;
  type: 'success' | 'error';
} => {
  const messageTypeToNameAndStyleMap = {
    DEADLINES_RESET_NOTIFICATION_SUCCESS: {
      message: (
        <FormattedMessage
          message={_t('Your deadlines have been reset. To continue learning, {goToCourseLink}.')}
          goToCourseLink={
            <Link href={`/learn/${courseSlug}`} variant="standard" className="link">
              {isRhymeProject
                ? _t('go to the Guided Project')
                : replaceCustomContent(_t('go to the #{course}'), { returnsString: true })}
            </Link>
          }
        />
      ),
      type: 'success',
    },
    DEADLINES_RESET_NOTIFICATION_ERROR: {
      message: _t('There was an issue with resetting your deadlines. Please try again later.'),
      type: 'error',
    },
    UNENROLLED_NOTIFICATION_SUCCESS: {
      message: (
        <FormattedMessage
          message={_t('You have unenrolled from {courseName}. To resume your learning, {goToCourseLink}.')}
          courseName={courseName}
          goToCourseLink={
            <Link href={`/learn/${courseSlug}`} variant="standard" className="link">
              {isRhymeProject
                ? _t('go to the Guided Project')
                : replaceCustomContent(_t('go to the #{course}'), { returnsString: true })}
            </Link>
          }
        />
      ),
      type: 'success',
    },
    UNENROLLED_NOTIFICATION_ERROR: {
      message: _t('There was an issue with unenrolling from this course or specialization. Please try again later.'),
      type: 'error',
    },
  };
  // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return messageTypeToNameAndStyleMap[messageTypeName];
};

const NotificationWrapper = ({
  messageTypeName,
  courseName,
  courseSlug,
  isNotificationVisible = true,
  isRhymeProject,
  replaceCustomContent,
}: Props) => {
  if (!messageTypeName || !isNotificationVisible) return null;
  const { message, type } =
    getMessageAndType({ messageTypeName, courseName, courseSlug, isRhymeProject, replaceCustomContent }) || {};

  return message ? (
    <PageGridContainer>
      <Grid item xs={12}>
        <PageNotification severity={type} role="alert">
          {message}
        </PageNotification>
      </Grid>
    </PageGridContainer>
  ) : null;
};

export default compose<Props, Omit<Props, 'replaceCustomContent'>>(withCustomLabelsByUserAndCourse)(
  NotificationWrapper
);
